import { exec, execForm, getApiToken } from '../utils';
import type {
  DetailsResult,
  UpdateParentSkuImagesExecVariables,
  UpdateParentSkuImagesExecResponse
} from '@src/types';

interface UpdateParentSkuImagesArgs extends UpdateParentSkuImagesExecVariables {
  files?: { [key: string]: File };
  studioFiles?: { [key: string]: File };
}

export const updateParentSkuImages = async (args: UpdateParentSkuImagesArgs): Promise<DetailsResult> => {
  const { files, studioFiles, token, ...updateFields } = args;

  const apiToken = await getApiToken(token);

  const query = `
      mutation updateParentSkuImages(
        $parentSku: String!
        $partnerCode: String!
        $token: String!
        $imagesByColorway: JSON
        $studioImagesByColorway: JSON
        $videosByColorway: JSON
      ) {
        restUser(token: $token) {
          partner(code: $partnerCode) {
            updateParentSkuImages(
              parentSku: $parentSku
              imagesByColorway: $imagesByColorway
              studioImagesByColorway: $studioImagesByColorway
              videosByColorway: $videosByColorway
            ) {
              productName
              productDescription
              availability
              isInconsistentAvailability
              departments
              categories
              partnerCategory
              gender {
                code
                name
              }
              msrpPrice
              taxCode
              brandDisplay
              rubricCode
              imagesByColorway
              studioImagesByColorway
              videosByColorway
              variants {
                colorCode
                colorDisplay
                variantCount
                skus
                sizeDisplays
                partnerImageUrls
                studioImageUrls
                otherVitalElements
              }
            }
          }
        }
      }
    `;

  const variables = {
    ...updateFields,
    token: apiToken,
  };

  // Merge partner files and studio files into a single file map
  // Studio files are prefixed with "studio_" to differentiate from partner files
  const allFiles: Record<string, File> = {};
  if (files) {
    Object.entries(files).forEach(([key, file]) => {
      allFiles[key] = file;
    });
  }
  if (studioFiles) {
    Object.entries(studioFiles).forEach(([key, file]) => {
      allFiles[`studio_${key}`] = file;
    });
  }
  // If there are files to upload, use execForm; otherwise use regular exec
  if (Object.keys(allFiles).length > 0) {
    const data = await execForm<typeof variables, UpdateParentSkuImagesExecResponse>(
      query,
      variables,
      allFiles
    );
    return data.restUser.partner.updateParentSkuImages;
  } else {
    const data = await exec<typeof variables, UpdateParentSkuImagesExecResponse>(
      query,
      { variables }
    );
    return data.restUser.partner.updateParentSkuImages;
  }
};
